import React, { useState, useEffect, JSX } from 'react';
import { Table, Button, Layout, Card, Col, Row, Spin, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BackendAPI } from '../../../axios';
import Sider from 'antd/es/layout/Sider';
import '../TxnCostSummary/Txnsummary.scss';
import axios from 'axios';
import Swal from 'sweetalert2';
import { EyeInvisibleOutlined, EyeOutlined, LoadingOutlined, LockOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import withReactContent from 'sweetalert2-react-content';
import { Form, useNavigate } from 'react-router-dom';
import UserNavbar from '../UserNavbar';
import { Content, Footer } from 'antd/es/layout/layout';
import { Controller } from 'react-hook-form';
import UserHeader from '../UserHeader';
const { Title } = Typography;
const THEME_COLOR = "#1515b4";

const MySwal = withReactContent(Swal);

interface PageSettingsProps {
    isDarkMode: boolean;
}

// Define the interface for the data
interface BillData {
    bill_no: number;
    bill_year: string;
    bill_quarter: string;
    bill_amount: number;
    bill_generation_date: string;
    surcharge: number;
    bill: string | null; // PDF data (Base64 or null)
    invoice_no: string;
    invoice_date: string;
    invoice_amount: number;
    update_by: string;
    update_timestamp: string;
}

function BillTable(): JSX.Element {
    // State to store API data
    const [collapsed, setCollapsed] = useState(false);
    const [isOnClickSlider, setOnclickSlider] = useState(false);
    const [data, setData] = useState<BillData[]>([]);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 830);
    const navigate = useNavigate();

    // Function to download the PDF
    const handleDownload = (base64String: string | null, billNo: number) => {
        if (!base64String) {
            alert(`No PDF available for Bill No: ${billNo}`);
            return;
        }
        // Create a Blob and download
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length).map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `Bill-${billNo}.pdf`;
        link.click();
    };

    // Define the columns
    const columns: ColumnsType<BillData> = [
        {
            title: 'Bill No',
            dataIndex: 'bill_no',
            key: 'bill_no',
            align: 'center',
        },
        {
            title: 'Year',
            dataIndex: 'bill_year',
            key: 'bill_year',
            align: 'center',
        },
        {
            title: 'Quarter',
            dataIndex: 'bill_quarter',
            key: 'bill_quarter',
            align: 'center',
        },
        {
            title: 'Amount',
            dataIndex: 'bill_amount',
            key: 'bill_amount',
            align: 'center',
            render: (text: number) => `₹ ${text.toFixed(2)}`, // Format as currency
        },
        {
            title: 'Generation Date',
            dataIndex: 'bill_generation_date',
            key: 'bill_generation_date',
            align: 'center',
        },
        {
            title: 'Surcharge',
            dataIndex: 'surcharge',
            key: 'surcharge',
            align: 'center',
            render: (text: number) => `₹ ${text.toFixed(2)}`, // Format as currency
        },
        {
            title: 'Invoice No',
            dataIndex: 'invoice_no',
            key: 'invoice_no',
            align: 'center',
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoice_date',
            key: 'invoice_date',
            align: 'center',
        },
        {
            title: 'Invoice Amount',
            dataIndex: 'invoice_amount',
            key: 'invoice_amount',
            align: 'center',
        },
        // {
        //   title: 'Download Bill',
        //   key: 'download',
        //   align: 'center',
        //   render: (_: any, record: BillData) => (
        //     <Button
        //       type="primary"
        //       onClick={() => handleDownload(record.bill, record.bill_no)}
        //       disabled={!record.bill} // Disable if no PDF is available
        //     >
        //       {record.bill ? 'Download PDF' : 'No PDF'}
        //     </Button>
        //   ),
        // },
    ];

    const handleCollapse = (value: any) => {
        setCollapsed(value);
        setOnclickSlider(!isOnClickSlider);
    };
    const handleLogout = async () => {
        try {
            const response = await BackendAPI.post("/user/logout", {}, { withCredentials: true });

            if (response.status === 200) {
                localStorage.removeItem("authToken");
                localStorage.removeItem("initialTime");
                localStorage.removeItem("lastSavedTime");
                localStorage.removeItem("remainingTime");
                navigate("/");
            }
        } catch (error) {
        }
    };

    let refreshToken: null;

    const getRefreshToken = async () => {
        try {
            const response = await BackendAPI.get("/user/refresh-token", { withCredentials: true });
            refreshToken = response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.request.status == 401) {
                    window.location.reload();
                }
            }
        }
    };

    const fetchCostSummaryData = async () => {
        const url = `/cost/reg_id`;
        const header = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${refreshToken}`,
            },
        };

        try {
            const response = await BackendAPI.get(url, header);
            setData(response.data);
        } catch (error) {
            let errorMessage = "Something went wrong. Please try again later."; // Default error message
            if (axios.isAxiosError(error)) {
                if (error.request) {
                    // Server responded with a status code outside 2xx range
                    if (error.request.status === 400) {
                        errorMessage = "Invalid request. Please check the entered details.";
                    } else if (error.request.status === 401) {
                        errorMessage = "Unauthorized access! Please log in again.";
                        handleLogout();
                    } else if (error.request.status === 403) {
                        errorMessage = "You do not have permission to perform this action.";
                    } else if (error.request.status === 404) {
                        errorMessage = "Requested resource not found!";
                    } else if (error.request.status === 500) {
                        errorMessage = "Server error! Please try again later.";
                    } else {
                        errorMessage = error.request.data?.message || "Something went wrong.";
                    }
                    MySwal.fire({
                        icon: 'error',
                        imageWidth: 100,
                        imageHeight: 100,
                        title: 'Error',
                        text: errorMessage,
                    });
                }
            }
            MySwal.fire({
                icon: 'error',
                imageWidth: 100,
                imageHeight: 100,
                title: 'Error Occurred!',
                text: errorMessage,
                confirmButtonColor: "#d33",
                confirmButtonText: "OK",
            });
        }
    };
    useEffect(() => {
        const executeFunctions = async () => {
            await getRefreshToken();
            await fetchCostSummaryData();
        };
        executeFunctions();
    }, []);
    useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth < 830);
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

    return (
        <>
            {loading && (
                <Spin spinning={true} style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 9999 }} indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            )}
            {/* <div>
                <UserHeader setIsOnClickSlider={setOnclickSlider} />
            </div> */}
            <Layout style={{ minHeight: '89vh', }}>

                <Sider
                    // collapsed={isOnClickSlider}
                    collapsed={isMobile ? true : isOnClickSlider}
                    // trigger={null} // disable default trigger
                    // collapsible
                    collapsedWidth={50}
                    // onCollapse={handleCollapse}
                    style={{
                        // height: '90vh',
                        background: 'white',
                        position: 'relative',
                        overflow: 'auto',
                        top: '0px',
                        paddingTop: '20px',
                    }}
                >
                    {/* Custom collapse icon at top */}

                    <div style={{ top: '0', position: 'sticky', height: '100%', }}>
                        {/* Add menu or other content here */}
                        {/* <div
                            style={{
                                position: 'absolute',
                                top: 0, textAlignLast: 'right',
                                cursor: 'pointer', width: '100%', padding: '6px 16px',
                            }}
                            onClick={handleCollapse}
                        >
                            {collapsed ? <MenuUnfoldOutlined style={{ fontSize: '30px', }} /> : <MenuFoldOutlined style={{ fontSize: '30px', }} />}
                        </div> */}
                        <UserNavbar />
                        {/* <div style={{ textAlignLast: 'center', transition: 'all 0.9s', display: isOnClickSlider ? 'none' : 'block', position: 'absolute', justifySelf: 'center', bottom: '2%', }}>
                            <img src={cdac_logo} alt="Status" style={{ width: '40%', height: 'auto' }} />
                            <h5 style={{ textAlign: 'center', margin: '0' }}>Center for Development of Advanced Computing (C-DAC)</h5>
                        </div> */}
                    </div>
                </Sider>
                <Layout>
                    <Content style={{ margin: '16px 16px 0' }}>
                        {/* <h1 style={{ textAlign: 'center', }}>Bills Table</h1> */}
                        <Title
                                    level={3}
                                    style={{
                                        color: THEME_COLOR,
                                        borderBottom: `3px solid ${THEME_COLOR}`,
                                        display: "inline-block",
                                        paddingBottom: 6,
                                        // marginBottom: 32,
                                    }}
                                >
                                    Bills Table
                                </Title>
                        <Table style={{ overflow: 'auto', }}
                        className="transactions-table"
                            columns={columns} // Table columns
                            dataSource={data} // Dynamic data
                            rowKey="bill_no" // Unique identifier for rows
                            bordered
                            pagination={false} // Disable pagination
                        />
                    </Content>
                    <Footer style={{ textAlign: 'center' }}>
                        ©2025 Copyright: C-DAC Mumbai. All Rights Reserved
                    </Footer>
                </Layout>
            </Layout>
        </>

    );
};

export default BillTable;
